(function () {
  'use strict';
  class HomepageDateOrder {
    constructor(root, panel) {
      this.root = root;
      this.panel = panel;
      this.size = Number(panel.dataset.pageSize);
      this.originalPage = Number(panel.dataset.page);
      this.originalCards = Array.from(panel.querySelectorAll('[data-feed-card]'));
      this.cards = new Map();
      this.pages = new Map();
      this.indexPromise = null;
      this.signature = '';
    }

    contextUrl(path) {
      const url = new URL(path, location.href);
      if (url.origin !== location.origin) throw new Error('Invalid product source');
      const context = new URL(location.href);
      for (const key of ['preview_theme_id', 'pb']) {
        if (context.searchParams.has(key)) url.searchParams.set(key, context.searchParams.get(key));
      }
      return url;
    }

    async index(signal) {
      if (this.indexPromise) return this.indexPromise;
      this.indexPromise = (async () => {
        const products = [];
        const seen = new Set();
        let url = this.contextUrl(this.panel.dataset.indexUrl);
        let page = 1;
        let total = 0;
        while (url) {
          url.searchParams.set('view', 'neko-upcoming-index');
          const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Unable to load product dates');
          const data = await response.json();
          if (data.collection !== this.panel.dataset.collection || data.page !== page || data.offset !== products.length || !Array.isArray(data.products)) throw new Error('Invalid product date index');
          total = data.total;
          for (const product of data.products) {
            if (typeof product.id !== 'string' || seen.has(product.id)) throw new Error('Duplicate product date index');
            seen.add(product.id);
            products.push({ id: product.id, date: product.date, position: products.length });
          }
          url = data.next ? this.contextUrl(data.next) : null;
          page++;
        }
        if (products.length !== total) throw new Error('Incomplete product date index');
        this.native = products;
        const initial = products.slice((this.originalPage - 1) * this.size, this.originalPage * this.size);
        if (initial.length === this.originalCards.length && initial.every((p, i) => p.id === this.originalCards[i].dataset.productId)) {
          this.originalCards.forEach(card => this.cards.set(card.dataset.productId, card.outerHTML));
          this.pages.set(this.originalPage, Promise.resolve());
        }
        function dateKey(value) {
          return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) ? value : '9999-99-99';
        }
        this.sorted = products.slice().sort((a, b) => dateKey(a.date).localeCompare(dateKey(b.date)) || a.position - b.position);
        let hash = 2166136261;
        for (const char of JSON.stringify(products)) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
        this.signature = String(hash >>> 0);
        return this.sorted;
      })().catch(error => { this.indexPromise = null; throw error; });
      return this.indexPromise;
    }

    seed(cards) {
      cards.forEach((html, id) => this.cards.set(id, html));
    }

    async sourcePage(page, signal) {
      if (!this.pages.has(page)) {
        const promise = (async () => {
          const url = this.contextUrl(location.href);
          url.searchParams.set('page', String(page));
          url.searchParams.set('section_id', this.root.dataset.homepageSection);
          const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Unable to load ordered products');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const panel = doc.querySelector('[data-homepage-section="' + CSS.escape(this.root.dataset.homepageSection) + '"] [data-upcoming-feed]');
          const cards = Array.from(panel?.querySelectorAll('[data-feed-card]') || []);
          const expected = this.native.slice((page - 1) * this.size, page * this.size);
          if (!panel || Number(panel.dataset.page) !== page || panel.dataset.collection !== this.panel.dataset.collection || cards.length !== expected.length || cards.some((card, i) => card.dataset.productId !== expected[i].id)) throw new Error('Product collection changed during loading');
          cards.forEach(card => this.cards.set(card.dataset.productId, card.outerHTML));
        })().catch(error => { this.pages.delete(page); throw error; });
        this.pages.set(page, promise);
      }
      await this.pages.get(page);
    }

    async page(page, signal) {
      const sorted = await this.index(signal);
      const selected = sorted.slice((page - 1) * this.size, page * this.size);
      const pages = new Set(selected.filter(product => !this.cards.has(product.id)).map(product => Math.floor(product.position / this.size) + 1));
      await Promise.all(Array.from(pages, sourcePage => this.sourcePage(sourcePage, signal)));
      const panel = document.createElement('section');
      panel.dataset.page = String(page);
      panel.dataset.total = String(sorted.length);
      panel.dataset.collection = this.panel.dataset.collection;
      for (const product of selected) {
        const template = document.createElement('template');
        template.innerHTML = this.cards.get(product.id) || '';
        const card = template.content.firstElementChild;
        if (!card?.matches('[data-feed-card]')) throw new Error('Missing ordered product');
        card.querySelectorAll('img').forEach(image => { image.loading = 'lazy'; image.removeAttribute('fetchpriority'); });
        panel.append(card);
      }
      if (page * this.size < sorted.length) {
        const next = document.createElement('a');
        const url = this.contextUrl(location.href);
        url.searchParams.delete('section_id');
        url.searchParams.set('page', String(page + 1));
        next.dataset.feedNext = '';
        next.href = url.pathname + url.search;
        panel.append(next);
      }
      return panel;
    }
  }
  window.NekoHomepageDateOrder = HomepageDateOrder;
})();
